import type * as Y from 'yjs';

import diff from 'fast-diff';

import { getYjsInstance } from './syncYjs';

export type YjsConfig =
  | {
      parent: Y.Map<any>;
      key: string;
    }
  | {
      parent: Y.Array<any>;
      key: number;
    };

/** Convert a fast-diff result to a YJS delta. */
const diffToDelta = (diffResult: diff.Diff[]) => {
  return diffResult
    .map(
      ([op, value]) =>
        ({
          [diff.INSERT]: { insert: value },
          [diff.EQUAL]: { retain: value.length },
          [diff.DELETE]: { delete: value.length },
        })[op],
    )
    .filter(Boolean);
};

export function updateYjsText(yjsConfig: YjsConfig, value: string) {
  const yText = getYjsInstance<Y.Text>(yjsConfig.parent, yjsConfig.key);
  if (!yText) return;
  const currentValue = yText.toJSON();
  const delta = diffToDelta(diff(currentValue, value));
  yText.applyDelta(delta);
}

const cloneYjsItem = (item: any) => {
  return typeof item === 'object' && item !== null && 'clone' in item
    ? (item as { clone: () => unknown }).clone()
    : item;
};

export function swapYjsArray(
  yjsConfig: YjsConfig,
  yjsDoc: Y.Doc,
  index1: number,
  index2: number,
) {
  const yjsArray = getYjsInstance<Y.Array<any>>(
    yjsConfig.parent,
    yjsConfig.key,
  );
  if (!yjsArray || index1 === index2) return;

  yjsDoc.transact(() => {
    const item1 = yjsArray.get(index1);
    const item2 = yjsArray.get(index2);
    if (typeof item1 === 'undefined' || typeof item2 === 'undefined') return;

    const item1Copy = cloneYjsItem(item1);
    const item2Copy = cloneYjsItem(item2);

    const lowerIndex = Math.min(index1, index2);
    const higherIndex = Math.max(index1, index2);

    yjsArray.delete(higherIndex, 1);
    yjsArray.delete(lowerIndex, 1);
    yjsArray.insert(lowerIndex, [index1 < index2 ? item2Copy : item1Copy]);
    yjsArray.insert(higherIndex, [index1 < index2 ? item1Copy : item2Copy]);
  });
}

export function moveYjsArray(
  yjsConfig: YjsConfig,
  yjsDoc: Y.Doc,
  sourceIndex: number,
  targetIndex: number,
) {
  const yjsArray = getYjsInstance<Y.Array<any>>(
    yjsConfig.parent,
    yjsConfig.key,
  );
  if (!yjsArray || sourceIndex === targetIndex) return;

  yjsDoc.transact(() => {
    const item = yjsArray.get(sourceIndex);
    if (typeof item === 'undefined') return;

    const itemClone = cloneYjsItem(item);
    yjsArray.delete(sourceIndex, 1);
    yjsArray.insert(targetIndex, [itemClone]);
  });
}

export function deleteYjsArrayItem(yjsConfig: YjsConfig, index: number) {
  const yjsArray = getYjsInstance<Y.Array<any>>(
    yjsConfig.parent,
    yjsConfig.key,
  );
  if (!yjsArray) return;
  yjsArray.delete(index, 1);
}

export function replaceAllArray(
  yjsConfig: YjsConfig,
  yjsDoc: Y.Doc,
  value: any[],
) {
  const yjsArray = getYjsInstance<Y.Array<any>>(
    yjsConfig.parent,
    yjsConfig.key,
  );
  if (!yjsArray) return;
  yjsDoc.transact(() => {
    yjsArray.delete(0, yjsArray.length);
    yjsArray.insert(0, value);
  });
}

export function insertToYjsArray(
  yjsConfig: YjsConfig,
  value: any,
  index?: number,
) {
  const yjsArray = getYjsInstance<Y.Array<any>>(
    yjsConfig.parent,
    yjsConfig.key,
  );
  if (!yjsArray) return;

  const newValue = Array.isArray(value) ? value : [value];

  if (typeof index === 'number') {
    yjsArray.insert(index, newValue);
  } else {
    yjsArray.push(newValue);
  }
}

export function replaceYjsArrayItem(
  yjsConfig: YjsConfig,
  yjsDoc: Y.Doc,
  index: number,
  value: any,
) {
  const yjsArray = getYjsInstance<Y.Array<any>>(
    yjsConfig.parent,
    yjsConfig.key,
  );
  if (!yjsArray) return;
  yjsDoc.transact(() => {
    yjsArray.delete(index, 1);
    yjsArray.insert(index, [value]);
  });
}
